import type { LevelGiantPlacement } from './levelGiantPlacements';

/**
 * Mutable DOM handles filled by LevelPlayfield / ProgressBar.
 * RAF writes transforms here without React setState.
 */
export type PlayfieldMotionBindings = {
  bgPanorama: HTMLElement | null;
  fgPanorama: HTMLElement | null;
  giant: HTMLElement | null;
  worldCamera: HTMLElement | null;
  playerRoot: HTMLElement | null;
  playerInner: HTMLElement | null;
  progressFill: HTMLElement | null;
  progressBar: HTMLElement | null;
  /** Positioned roots for path-moving obstacles/decors (id → element). */
  entities: Map<string, HTMLElement>;
  canvasTopPx: number;
  displayScale: number;
  giantPlacement: LevelGiantPlacement | null;
  routeEndS: number;
  courierScreenX: number;
  spawnSvgX: number;
  startPointSvgX: number;
  viewBoxHeight: number;
  /** Progress bar full width in design px (ProgressBar BAR_WIDTH). */
  progressBarWidth: number;
};

export function createPlayfieldMotionBindings(): PlayfieldMotionBindings {
  return {
    bgPanorama: null,
    fgPanorama: null,
    giant: null,
    worldCamera: null,
    playerRoot: null,
    playerInner: null,
    progressFill: null,
    progressBar: null,
    entities: new Map(),
    canvasTopPx: 0,
    displayScale: 1,
    giantPlacement: null,
    routeEndS: 1,
    courierScreenX: 0,
    spawnSvgX: 0,
    startPointSvgX: 0,
    viewBoxHeight: 1,
    progressBarWidth: 800.3734,
  };
}

export function applyPlayfieldCamera(
  bindings: PlayfieldMotionBindings,
  translateX: number,
): void {
  const { canvasTopPx, displayScale, giantPlacement } = bindings;
  const bgTransform = `translate(${translateX}px, ${canvasTopPx}px) scale(${displayScale})`;
  if (bindings.bgPanorama != null) {
    bindings.bgPanorama.style.transform = bgTransform;
  }
  if (bindings.fgPanorama != null) {
    bindings.fgPanorama.style.transform = `translateX(${translateX}px) scale(${displayScale})`;
  }
  if (bindings.giant != null && giantPlacement != null) {
    const gx = translateX + giantPlacement.left * displayScale;
    const gy = canvasTopPx + giantPlacement.top * displayScale;
    bindings.giant.style.transform = `translate(${gx}px, ${gy}px) scale(${displayScale})`;
  }
  if (bindings.worldCamera != null) {
    bindings.worldCamera.style.transform = `translate(${translateX}px, ${canvasTopPx}px)`;
  }
}

export function applyPlayerPose(
  bindings: PlayfieldMotionBindings,
  leftPx: number,
  topPx: number,
  tiltDeg: number,
): void {
  if (bindings.playerRoot != null) {
    bindings.playerRoot.style.left = `${leftPx}px`;
    bindings.playerRoot.style.top = `${topPx}px`;
  }
  if (bindings.playerInner != null) {
    bindings.playerInner.style.transform = `rotate(${tiltDeg}deg)`;
  }
}

export function applyEntityPose(
  bindings: PlayfieldMotionBindings,
  id: string,
  leftPx: number,
  topPx: number,
): void {
  const el = bindings.entities.get(id);
  if (el == null) {
    return;
  }
  el.style.left = `${leftPx}px`;
  el.style.top = `${topPx}px`;
}

export function applyProgressFill(
  bindings: PlayfieldMotionBindings,
  progress01: number,
): void {
  const progress = Math.max(0, Math.min(1, progress01));
  const width = progress * bindings.progressBarWidth;
  if (bindings.progressFill != null) {
    bindings.progressFill.style.width = `${width}px`;
    bindings.progressFill.style.display = width > 0 ? '' : 'none';
  }
  if (bindings.progressBar != null) {
    bindings.progressBar.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
  }
}
